import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { DatePicker, Space, Card, Row, Col, Button, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/en";
import useApiCall from "../common/api-call";
import { getAssistantStatistics } from "./statistics-service";

dayjs.locale("en");

const AssistantStatisticsView = () => {
  const { apiCall } = useApiCall();
  const [filteredData, setFilteredData] = useState([]);

  // Initialize dates
  const today = dayjs();
  const thirtyDaysAgo = dayjs().subtract(30, "day");

  const [startDate, setStartDate] = useState(thirtyDaysAgo.format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(today.format("YYYY-MM-DD"));
  const [startDateValue, setStartDateValue] = useState(thirtyDaysAgo);
  const [endDateValue, setEndDateValue] = useState(today);

  const fetchStatisticsData = async (start = startDate, end = endDate) => {
    try {
      const response = await getAssistantStatistics(apiCall, start, end);

      // Handle different possible response structures
      if (
        response &&
        response.statistics &&
        Array.isArray(response.statistics)
      ) {
        setFilteredData(response.statistics);
      } else if (response && Array.isArray(response)) {
        // Fallback for direct array response
        setFilteredData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Another possible structure
        setFilteredData(response.data);
      } else {
        console.log("Unexpected response structure:", response);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching assistant statistics:", error);
      setFilteredData([]);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const handleFetchData = () => {
    fetchStatisticsData(startDate, endDate);
  };

  // Calculate number of days between start and end date
  const calculateDaysBetween = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const timeDiff = endDateObj.getTime() - startDateObj.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
  };

  const daysBetween = calculateDaysBetween(startDate, endDate);

  const dayData = Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      day: date.toISOString().split("T")[0],
      requestCount: 0,
      quickCommandCount: 0,
      assistantCount: 0,
      errorCount: 0,
      tokenInputCount: 0,
      tokenOutputCount: 0,
    };
  });

  filteredData?.forEach(item => {
    // Use 'date' field from API response instead of 'time'
    const itemDate = item.date || "";
    const dateIndex = dayData.findIndex(day => day.day === itemDate);
    if (dateIndex !== -1) {
      dayData[dateIndex].requestCount += item.requestCount || 0;
      dayData[dateIndex].quickCommandCount += item.quickCommandCount || 0;
      dayData[dateIndex].assistantCount += item.assistantCount || 0;
      dayData[dateIndex].errorCount += item.errorCount || 0;
      dayData[dateIndex].tokenInputCount += item.tokenInputCount || 0;
      dayData[dateIndex].tokenOutputCount += item.tokenOutputCount || 0;
    }
  });

  const maxRequestCount = Math.max(
    ...dayData.map(item => item.requestCount),
    0,
  );
  const yAxisMax = maxRequestCount > 0 ? Math.ceil(maxRequestCount * 1.1) : 40;

  // Chart options for main statistics
  const getMainChartOption = () => ({
    title: {
      text: "Assistant Statistics",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        const { name } = params[0];
        let tooltipContent = `Date: ${name}<br />`;
        params.forEach(param => {
          tooltipContent += `${param.seriesName}: ${param.value}<br />`;
        });
        return tooltipContent;
      },
    },
    legend: {
      data: [
        "Request Count",
        "Quick Command Count",
        "Assistant Count",
        "Error Count",
      ],
      top: "12%",
    },
    grid: {
      top: "30%",
      left: "15%",
      right: "10%",
      bottom: "15%",
    },
    xAxis: {
      type: "category",
      data: dayData.map(item => item.day),
      axisLabel: {
        interval: Math.max(1, Math.floor(daysBetween / 10)),
        rotate: 45,
        margin: 10,
        formatter: value => dayjs(value).format("MM-DD"),
        showMaxLabel: true,
      },
      axisTick: {
        alignWithLabel: true,
        interval: Math.max(1, Math.floor(daysBetween / 15)),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: yAxisMax,
      interval: Math.max(1, Math.ceil(yAxisMax / 10)),
      axisLabel: {
        formatter: value => Math.round(value).toLocaleString(),
        padding: [5, 5, 5, 5],
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#ccc",
        },
      },
    },
    series: [
      {
        name: "Request Count",
        type: "line",
        data: dayData.map(item => item.requestCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#4caf50",
        },
      },
      {
        name: "Quick Command Count",
        type: "line",
        data: dayData.map(item => item.quickCommandCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#2196f3",
        },
      },
      {
        name: "Assistant Count",
        type: "line",
        data: dayData.map(item => item.assistantCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#ff9800",
        },
      },
      {
        name: "Error Count",
        type: "line",
        data: dayData.map(item => item.errorCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#f44336",
        },
      },
    ],
  });

  // Chart options for token data
  const getTokenChartOption = () => {
    const maxTokenCount = Math.max(
      ...dayData.map(item =>
        Math.max(item.tokenInputCount, item.tokenOutputCount),
      ),
      0,
    );
    const tokenYAxisMax =
      maxTokenCount > 0 ? Math.ceil(maxTokenCount * 1.1) : 1000;

    return {
      title: {
        text: "Token Usage",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${
              param.seriesName
            }: ${param.value.toLocaleString()}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: ["Token Input", "Token Output"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: tokenYAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
          padding: [5, 5, 5, 5],
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: "Token Input",
          type: "line",
          data: dayData.map(item => item.tokenInputCount),
          animation: true,
          lineStyle: {
            width: 2,
          },
          itemStyle: {
            color: "#9c27b0",
          },
        },
        {
          name: "Token Output",
          type: "line",
          data: dayData.map(item => item.tokenOutputCount),
          animation: true,
          lineStyle: {
            width: 2,
          },
          itemStyle: {
            color: "#ff5722",
          },
        },
      ],
    };
  };

  // Chart options for error ratio
  const getErrorRatioChartOption = () => {
    const errorRatioData = dayData.map(item => {
      if (item.assistantCount === 0) return 0;
      return (item.errorCount / item.assistantCount) * 100;
    });

    const maxErrorRatio = Math.max(...errorRatioData, 0);
    const errorRatioYAxisMax =
      maxErrorRatio > 0 ? Math.ceil(maxErrorRatio * 1.1) : 10;

    return {
      title: {
        text: "Error Ratio (Error/Assistant %)",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name, value } = params[0];
          return `Date: ${name}<br />Error Ratio: ${value.toFixed(2)}%`;
        },
      },
      grid: {
        top: "25%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: errorRatioYAxisMax,
        axisLabel: {
          formatter: value => `${Math.round(value)}%`,
          padding: [5, 5, 5, 5],
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: "Error Ratio",
          type: "line",
          data: errorRatioData,
          animation: true,
          lineStyle: {
            width: 3,
          },
          itemStyle: {
            color: "#e91e63",
          },
          areaStyle: {
            opacity: 0.3,
            color: "#e91e63",
          },
        },
      ],
    };
  };

  useEffect(() => {
    // Initialize main chart
    const mainChartDom = document.getElementById("assistant-chart");
    if (mainChartDom) {
      const mainChart = echarts.init(mainChartDom);
      mainChart.setOption(getMainChartOption());

      // Initialize token chart
      const tokenChartDom = document.getElementById("token-chart");
      if (tokenChartDom) {
        const tokenChart = echarts.init(tokenChartDom);
        tokenChart.setOption(getTokenChartOption());

        // Initialize error ratio chart
        const errorRatioChartDom = document.getElementById("error-ratio-chart");
        if (errorRatioChartDom) {
          const errorRatioChart = echarts.init(errorRatioChartDom);
          errorRatioChart.setOption(getErrorRatioChartOption());

          const handleResize = () => {
            mainChart.resize();
            tokenChart.resize();
            errorRatioChart.resize();
          };

          window.addEventListener("resize", handleResize);

          return () => {
            mainChart.dispose();
            tokenChart.dispose();
            errorRatioChart.dispose();
            window.removeEventListener("resize", handleResize);
          };
        }
      }
    }
  }, [filteredData, startDate, endDate]);

  // Calculate totals for summary cards
  const totalRequestCount = dayData.reduce(
    (sum, item) => sum + item.requestCount,
    0,
  );
  const totalQuickCommandCount = dayData.reduce(
    (sum, item) => sum + item.quickCommandCount,
    0,
  );
  const totalAssistantCount = dayData.reduce(
    (sum, item) => sum + item.assistantCount,
    0,
  );
  const totalErrorCount = dayData.reduce(
    (sum, item) => sum + item.errorCount,
    0,
  );
  const totalTokenInputCount = dayData.reduce(
    (sum, item) => sum + item.tokenInputCount,
    0,
  );
  const totalTokenOutputCount = dayData.reduce(
    (sum, item) => sum + item.tokenOutputCount,
    0,
  );
  const errorRate =
    totalAssistantCount > 0
      ? ((totalErrorCount / totalAssistantCount) * 100).toFixed(1)
      : 0;

  return (
    <div style={{ flexGrow: 1, height: "100%", margin: 16, paddingBottom: 80 }}>
      {/* Date Selection Controls */}
      <Card
        style={{
          marginBottom: 24,
          border: "1px solid #e0e0e0",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography.Title level={5} style={{ marginBottom: 16, color: "#333" }}>
          Date Range Selection
        </Typography.Title>
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Start Date
              </Typography.Text>
              <DatePicker
                value={startDateValue}
                onChange={(date) => {
                  setStartDateValue(date);
                  setStartDate(date ? date.format("YYYY-MM-DD") : "");
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
            />
            </Space>
          </Col>
          <Col xs={24} sm={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                End Date
              </Typography.Text>
              <DatePicker
                value={endDateValue}
                onChange={(date) => {
                  setEndDateValue(date);
                  setEndDate(date ? date.format("YYYY-MM-DD") : "");
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
            />
            </Space>
          </Col>
          <Col xs={24} sm={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleFetchData}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24, justifyContent: "center" }}>
        <Col xs={12} sm={6} md={4}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Total Requests
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#4caf50" }}>
            {totalRequestCount.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Assistant Count
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ff9800" }}>
            {totalAssistantCount.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Error Count
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#f44336" }}>
            {totalErrorCount.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Error Rate
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#e91e63" }}>
            {errorRate}%
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Input Tokens
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#9c27b0" }}>
            {totalTokenInputCount.toLocaleString()}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card style={{ textAlign: "center", maxWidth: 250, minWidth: 150 }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333", marginBottom: 8 }}>
            Output Tokens
            </div>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ff5722" }}>
            {totalTokenOutputCount.toLocaleString()}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts in Column Layout */}
      <Row gutter={24}>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div
              id="assistant-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div id="token-chart" style={{ width: "100%", height: "100%" }} />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ width: "100%", height: "450px" }} className="chart-container">
            <div
              id="error-ratio-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AssistantStatisticsView;
